import { useState, useEffect } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import HrForm from "@/components/hr-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Mail, Phone } from "lucide-react";
import { supabase } from "../../supabaseClient";

export default function Hrs() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();
  const [hrs, setHrs] = useState<Hr[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation("/login");
    }
  }, [user, isLoading, setLocation]);

  useEffect(() => {
    const fetchHrs = async () => {
      const { data, error } = await supabase.from("hr").select("*");

      if (error) {
        console.error("Error fetching HRs:", error.message);
      } else {
        setHrs(data);
      }
    };

    fetchHrs();
  }, []);

  const handleSuccess = (newHr: Hr) => {
    setOpen(false);
    setHrs((prevHrs) => [...prevHrs, newHr]);
  };

  const filteredHrs = hrs.filter((hr) =>
    hr.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) return <div>Loading...</div>;
  if (!user) return null;

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">HRs</h2>
            <p className="text-muted-foreground">Manage your HR relationships</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add HR
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New HR</DialogTitle>
              </DialogHeader>
              <HrForm onSuccess={handleSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          <input
            type="text"
            placeholder="Search HR..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredHrs.map((hr) => (
            <Card
              key={hr.id}
              className="relative cursor-pointer"
              onClick={() => {
                setLocation(`/hr-details/${hr.id}`);
              }}
            >
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{hr.name || "Unknown HR"}</h3>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Mail className="mr-2 h-4 w-4" />
                      {hr.email || "No email available"}
                    </div>
                    {hr.phone && (
                      <div className="flex items-center">
                        <Phone className="mr-2 h-4 w-4" />
                        {hr.phone}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
